import { ArrowDropUp } from '@mui/icons-material';
import Tooltip from 'components/Tooltip';
import { useAtom, useAtomValue } from 'jotai';
import { CompanyData } from 'lib/api/company/types';
import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import './styles.css';
import 'views/Admin/Dashboard/styles.css';
import {
  companyLogoAtom,
  companyDataAtom,
  companyIdAtom,
  currentCompanyAtom,
  useOnCompanyChangeResetAtoms,
} from 'views/state';

type Props = {
  setCurrentCompany: (id) => void;
};

export interface CompanyOption {
  readonly value: number;
  readonly label: string;
}

const LogoCompaniesDropdown = ({ setCurrentCompany }: Props) => {
  const [companyId, setCompanyId] = useAtom(companyIdAtom);
  const [companyLogo, setCompanyLogo] = useAtom(companyLogoAtom);
  const [companyName, setCompanyName] = useAtom(currentCompanyAtom);
  const companiesData = useAtomValue(companyDataAtom);
  const [selectedCompanyOption, setSelectedCompanyOption] =
    useState<CompanyOption>();
  const [logoVisible, setLogoVisible] = useState<boolean>(true);
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [dropdownEnabled, setDropdownEnabled] = useState(false);
  const resetOnCompanyChangeAtoms = useOnCompanyChangeResetAtoms();

  useEffect(() => {
    if (companiesData?.length > 0) {
      let currentCompany: CompanyData | undefined = undefined;
      currentCompany = companiesData.find((x) => x.id === companyId);

      if (!currentCompany) {
        currentCompany = companiesData[0];
      }

      if (currentCompany) {
        let { id, display_name, logo } = currentCompany;
        updateCompanyAtoms(id, display_name, logo);
        setSelectedCompanyOption(toCompanyOption({ value: id, display_name }));
      }

      if (companiesData?.length > 1) {
        setDropdownEnabled(true);
      }
    }
  }, [companiesData, companyId, companyLogo, companyName]);

  const toCompanyOption = (dataItem: {
    value: number;
    display_name: string;
  }) => {
    return { value: dataItem.value, label: dataItem.display_name };
  };

  const updateCompanyAtoms = (id, display_name, logo) => {
    const oldCompanyId = companyId;
    // This is required for the class base component, once it is refactored in functional component
    // TO DO: remove this function dependency, after refactoring
    setCurrentCompany(id);
    // Set the localstorage value using atoms
    setCompanyId(id);
    setCompanyName(display_name);
    setCompanyLogo(logo);

    setLogoVisible(true);
    setIsHovered(false);

    if (oldCompanyId !== id) {
      // !Reset atoms only if companyId is changing
      resetOnCompanyChangeAtoms();

      // reset the url query string params on company change to avoid confusion as the data will be different for different companies
      const url = new URL(window.location.href);
      url.search = '';
      window.history.replaceState({}, '', url.toString());
    }
  };

  const updateCompany = (e) => {
    setSelectedCompanyOption(e);
    const dataItem = companiesData.filter((x) => x.id === e.value)[0];
    updateCompanyAtoms(dataItem.id, dataItem.display_name, dataItem.logo);
  };

  const renderLogo = () => {
    if (
      companyLogo !== '' &&
      companyLogo !== null &&
      companyLogo !== undefined &&
      companyLogo.indexOf('assets/sp-default.png') === -1
    ) {
      return (
        <div
          className="d-flex align-items-center justify-content-center company-logo-container"
          onClick={() => setLogoVisible(false)}
        >
          <img
            style={{ borderRadius: '0' }}
            className="evisions_logo"
            src={companyLogo}
            alt={companyName + '-logo'}
          />
          {dropdownEnabled && (
            <ArrowDropUp
              className={`dropdown-icon ${isHovered ? 'active' : ''}`}
            />
          )}
        </div>
      );
    } else {
      return (
        <div
          className="d-flex align-items-center justify-content-center company-logo-container"
          onClick={() => setLogoVisible(false)}
        >
          <span>{companyName}</span>
          {dropdownEnabled && (
            <ArrowDropUp
              className={`dropdown-icon ${isHovered ? 'active' : ''}`}
            />
          )}
        </div>
      );
    }
  };

  const renderDropdown = () => {
    return (
      <div
        id="logo-dropdown"
        className="logo-dropdown-container px-2"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {logoVisible || !dropdownEnabled ? (
          renderLogo()
        ) : (
          <Select
            className={`custom-360-dropdown w-fit-content`}
            options={
              companiesData?.map((item) => {
                return toCompanyOption({
                  value: item.id,
                  display_name: item.display_name,
                });
              }) || []
            }
            value={selectedCompanyOption}
            onChange={updateCompany}
            styles={{
              control: (provided, state) => ({
                ...provided,
                background: 'transparent', // Make the background transparent
                border: state.isFocused ? '1px solid #ccc' : '1px solid #ccc', // Border color changes on focus
                borderRadius: '5px', // Rounded corners
                boxShadow: 'none', // Remove shadow
                color: '#fff', // Set text color to white
              }),
              dropdownIndicator: (provided, _state) => ({
                ...provided,
                color: '#fff', // Set arrow color to white when focused
              }),
              option: (provided, state) => ({
                ...provided,
                backgroundColor: state.isSelected
                  ? '#007bff'
                  : state.isFocused
                    ? '#007bff33'
                    : 'transparent',
                color: state.isSelected ? '#fff' : '#000',
                cursor: 'pointer',
              }),
              singleValue: (provided) => ({
                ...provided,
                color: '#fff', // Set text color to white for the selected value
              }),
              input: (provided) => ({
                ...provided,
                color: '#fff', // Set text color to white when typing
              }),
              placeholder: (provided) => ({
                ...provided,
                color: '#fff', // Set text color to white for the placeholder
              }),
              menuPortal: (base) => ({ ...base, zIndex: 9999 }), // Set z-index for the menu portal
            }}
            closeMenuOnSelect={true}
            menuPortalTarget={document.body}
          />
        )}
      </div>
    );
  };

  return dropdownEnabled ? (
    <div style={{ textTransform: 'none', fontVariant: 'none' }}>
      <Tooltip
        id="logo-dropdown"
        title={companyName}
        tooltip="Click to switch company"
      >
        {renderDropdown()}
      </Tooltip>
    </div>
  ) : (
    renderDropdown()
  );
};

export default LogoCompaniesDropdown;
